import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fileService } from '../services/file.service';
import { toast } from 'react-hot-toast';

/**
 * Hook pour uploader un fichier unique
 */
export const useUploadFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      options,
    }: {
      file: File;
      options?: { entity_type?: string; entity_id?: string };
    }) => fileService.uploadFile(file, options),
    onSuccess: () => {
      toast.success('Fichier uploadé avec succès');
      queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de l\'upload du fichier';
      toast.error(message);
    },
  });
};

/**
 * Hook pour uploader une photo de profil
 */
export const useUploadProfilePicture = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      entity_id,
    }: {
      file: File;
      entity_id?: string;
    }) => fileService.uploadProfilePicture(file, { entity_id }),
    onSuccess: () => {
      toast.success('Photo de profil uploadée avec succès');
      queryClient.invalidateQueries({ queryKey: ['player-profile'] });
      queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de l\'upload de la photo';
      toast.error(message);
    },
  });
};

/**
 * Hook pour uploader une photo de couverture
 */
export const useUploadCoverPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      entity_id,
    }: {
      file: File;
      entity_id?: string;
    }) => fileService.uploadCoverPhoto(file, { entity_id }),
    onSuccess: () => {
      toast.success('Photo de couverture uploadée avec succès');
      queryClient.invalidateQueries({ queryKey: ['player-profile'] });
      queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de l\'upload de la photo';
      toast.error(message);
    },
  });
};

/**
 * Hook pour uploader plusieurs fichiers
 */
export const useUploadMultipleFiles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      files,
      options,
    }: {
      files: File[];
      options?: { entity_type?: string; entity_id?: string };
    }) => fileService.uploadMultipleFiles(files, options),
    onSuccess: (data) => {
      toast.success(`${data.length} fichier(s) uploadé(s) avec succès`);
      queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de l\'upload des fichiers';
      toast.error(message);
    },
  });
};

/**
 * Hook pour uploader des médias de post
 */
export const useUploadPostMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      files,
      entity_id,
    }: {
      files: File[];
      entity_id?: string;
    }) => fileService.uploadPostMedia(files, { entity_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de l\'upload des médias';
      toast.error(message);
    },
  });
};

/**
 * Hook pour supprimer un fichier
 */
export const useDeleteFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (filename: string) => fileService.deleteFile(filename),
    onSuccess: () => {
      toast.success('Fichier supprimé avec succès');
      queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la suppression du fichier';
      toast.error(message);
    },
  });
};

/**
 * Hook pour lister les fichiers
 */
export const useListFiles = (filters?: {
  entity_type?: string;
  entity_id?: string;
  file_type?: string;
  uploaded_by?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['files', filters],
    queryFn: () => fileService.listFiles(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook pour récupérer les métadonnées d'un fichier
 */
export const useFileMetadata = (filename?: string) => {
  return useQuery({
    queryKey: ['file-metadata', filename],
    queryFn: () => fileService.getFileMetadata(filename!),
    enabled: !!filename,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};
